/**
 * The lambda language: how a program is split into tokens and what each one is
 * coloured with.
 *
 * Kept apart from the editor so that the rules can be tested without a
 * document. The parser tracks one thing, the nesting depth of parentheses,
 * which is what lets each pair be painted its own colour the way an editor
 * with bracket pair colourisation does; everything else in this language is a
 * name, a lambda, punctuation or a comment.
 *
 * It also knows which names already resolve, because that is a property of a
 * name rather than a layer on top of it: a definition the player can use is
 * painted differently from one they have not written yet, and no second pass
 * over the text is needed to say so. What a lambda binds is followed as well,
 * so a parameter that shares a name with a definition - which is what
 * `fix (\map f xs. ...)` does - is a local name where it is in scope and the
 * definition only outside it.
 *
 * It knows where the parentheses are, too, which is what the editor asks when
 * it has to say which pair the caret is inside: a stream parser leaves no
 * nesting behind to walk up, so that is worked out here, where the comment rule
 * and the bracket tokens already live.
 *
 * The colours themselves are the site's, read out of `app.css`, so the editor
 * and the chrome around it cannot drift apart.
 */

import { HighlightStyle, StreamLanguage, type StreamParser } from '@codemirror/language';
import type { Text } from '@codemirror/state';
import { Tag, tags } from '@lezer/highlight';

/** What starts a comment: the rest of the line is prose rather than code. */
const COMMENT = '--';

/** One tag per bracket colour; the parser picks by depth. */
const bracketTags = [1, 2, 3, 4, 5, 6].map((level) => Tag.define(`bracket${level}`));

/** The name on the left of an `=`, which is being introduced rather than used. */
const definitionTag = Tag.define('definitionName');

/** A name that is already defined somewhere the player can reach. */
const knownTag = Tag.define('knownName');

/**
 * A name a lambda binds.
 *
 * It has a tag of its own even though it is painted like any other name, so
 * that what the tokenizer decided about a shadowed name can be seen.
 */
const boundTag = Tag.define('boundName');

/** The state a parser starts a document in. */
export function blankState(): ParserState {
  return { depth: 0, binding: false, bound: [] };
}

export interface ParserState {
  /** How many parentheses are open, which is what colours the next one. */
  depth: number;
  /** Whether the names being read are the parameters of one lambda. */
  binding: boolean;
  /** The names lambdas have bound, and the depth each was bound at. */
  bound: { name: string; depth: number }[];
}

const tokenTable = Object.fromEntries([
  ...bracketTags.map((tag, index) => [`bracket${index + 1}`, tag]),
  ['definitionName', definitionTag],
  ['knownName', knownTag],
  ['boundName', boundTag],
]);

/** Builds the tokenizer for the names that resolve in the document it reads. */
export function lambdaParserFor(known: ReadonlySet<string>): StreamParser<ParserState> {
  return {
    name: 'lambda',
    startState: blankState,
    /*
     * Only parentheses: the language has no other brackets, and a lone quote is
     * part of a name rather than the start of a string.
     *
     * The list holds the opening tokens, one per pair, which is what
     * CodeMirror reads here. Writing the two characters of the pair out as one
     * string instead lists the closing paren as a bracket in its own right,
     * and a bracket that opens and closes with itself is read as a quote:
     * typing it around a selection then puts a bracket on each side of the
     * text, where the player asked for one.
     */
    languageData: { closeBrackets: { brackets: ['('] } },
    /**
     * Indentation for a new line.
     *
     * A line only continues the term above it when it is indented deeper than
     * that definition's name: a line at the name's own column starts the next
     * definition, and the parser says so. A new line therefore has to be at
     * least one level in when it has a term to continue, which is when the
     * break leaves a term unfinished or when what follows the break comes down
     * with it. A break at the end of a term, and one that moves nothing but a
     * comment, keep the indentation of the line they came from, which is what
     * CodeMirror does when this returns nothing.
     *
     * Within a term the line keeps the level it was at rather than gaining one
     * for every break, so a body written at one level stays there. What moves
     * it is what the line itself opens: a bracket still open at the break takes
     * the next line a level deeper, and a bracket closed before the break takes
     * it a level back, which is how a lambda or a parenthesised term is written
     * across lines without counting spaces by hand.
     */
    indent(_state, _textAfter, context) {
      // The simulated break is the line the new one is being opened under,
      // which is the only case this has an opinion about.
      const at = context.simulatedBreak;
      if (at === null) {
        return null;
      }
      const line = context.state.doc.lineAt(at);
      const column = at - line.from;
      if (!indentsAfter(line.text, column)) {
        // The term ends here. If it ends the definition as well, the line
        // after it is a new definition, which belongs at the margin: this
        // line's indentation is a continuation's, and copying it would put
        // the next definition inside the one that just ended.
        return endsDefinition(context.state.doc, at) ? 0 : null;
      }
      return indentationFor(line.text, column, context.unit);
    },
    tokenTable,
    token(stream, state) {
      if (stream.eatSpace()) return null;
      if (stream.match(COMMENT)) {
        stream.skipToEnd();
        return 'comment';
      }
      // The end of a lambda's parameters.
      if (stream.match('.')) {
        state.binding = false;
        return 'punctuation';
      }
      if (stream.match('\\')) {
        state.binding = true;
        return 'keyword';
      }
      if (stream.match('(')) {
        state.depth += 1;
        state.binding = false;
        return `bracket${((state.depth - 1) % bracketTags.length) + 1}`;
      }
      if (stream.match(')')) {
        // A closing paren takes the colour of the one it closes, and ends the
        // scope of everything a lambda bound inside it.
        state.depth = Math.max(0, state.depth - 1);
        state.binding = false;
        state.bound = state.bound.filter((entry) => entry.depth <= state.depth);
        return `bracket${(state.depth % bracketTags.length) + 1}`;
      }
      if (stream.match(/^[A-Za-z_][A-Za-z0-9_']*/)) {
        const name = stream.current();
        // A definition's name is the line's first token with an `=` after it.
        // The equations a statement lists, `not true = false`, are cases and
        // not definitions: the name before their `=` is an argument, and the
        // one that opens the line is applied, not defined.
        const opensLine = /^[ \t]*$/.test(stream.string.slice(0, stream.start));
        if (opensLine && /^[ \t]*=/.test(stream.string.slice(stream.pos))) {
          state.binding = false;
          if (state.depth === 0) {
            state.bound = [];
          }
          return 'definitionName';
        }
        if (state.binding) {
          state.bound.push({ name, depth: state.depth });
          return 'boundName';
        }
        return state.bound.some((entry) => entry.name === name) ? 'boundName'
          : known.has(name)
            ? 'knownName'
            : 'variableName';
      }
      if (stream.match(/^[=;]/)) return 'punctuation';
      stream.next();
      return null;
    },
  };
}

/** The tokenizer with nothing known, which is what the tests drive. */
export const lambdaParser = lambdaParserFor(new Set());

/** The language as it reads a document where these names resolve. */
export function lambdaLanguageFor(known: ReadonlySet<string>): StreamLanguage<ParserState> {
  return StreamLanguage.define(lambdaParserFor(known));
}

export const lambdaHighlight = HighlightStyle.define([
  { tag: tags.keyword, color: 'var(--code-keyword)', fontWeight: '600' },
  { tag: definitionTag, color: 'var(--code-definition)' },
  { tag: knownTag, color: 'var(--code-known)' },
  { tag: boundTag, color: 'var(--code-text)' },
  { tag: tags.variableName, color: 'var(--code-text)' },
  { tag: tags.comment, color: 'var(--code-comment)', fontStyle: 'italic' },
  { tag: tags.punctuation, color: 'var(--code-punct)' },
  ...bracketTags.map((tag, index) => ({ tag, color: `var(--bracket-${index + 1})` })),
]);

/** A pair of parentheses, by where the two characters are. */
export interface BracketPair {
  open: number;
  close: number;
}

/**
 * The innermost pair of parentheses around `pos`, or `null`.
 *
 * A stream parser leaves no nesting behind to walk up, so this counts it: going
 * back from the caret, a `)` deepens the group it is in and the first `(` at
 * depth zero is the one that opened it; going forward from there, the `)` that
 * brings the depth back to zero closes it. Both ends have to be on either side
 * of `pos`, and neither does when the caret is in a comment, because a comment
 * is prose and its parentheses are not brackets.
 */
export function enclosingPair(doc: Text, pos: number): BracketPair | null {
  if (inComment(doc, pos)) {
    return null;
  }
  const open = enclosingOpen(doc, pos);
  if (open === null) {
    return null;
  }
  const close = closingBracket(doc, open);
  return close !== null && close > pos ? { open, close } : null;
}

/** Whether the position sits inside a comment. */
function inComment(doc: Text, pos: number): boolean {
  const line = doc.lineAt(pos);
  const start = line.text.indexOf(COMMENT);
  return start !== -1 && line.from + start < pos;
}

/** The code part of a line, which ends where a comment starts. */
function codeBefore(text: string, upTo: number): string {
  const start = text.indexOf(COMMENT);
  return start === -1 || start > upTo ? text.slice(0, upTo) : text.slice(0, start);
}

/** The parenthesis that opened the group `pos` is in, if there is one. */
function enclosingOpen(doc: Text, pos: number): number | null {
  const caret = doc.lineAt(pos);
  let depth = 0;
  for (let number = caret.number; number >= 1; number -= 1) {
    const line = doc.line(number);
    const upTo = number === caret.number ? pos - line.from : line.text.length;
    const code = codeBefore(line.text, upTo);
    for (let index = code.length - 1; index >= 0; index -= 1) {
      if (code[index] === ')') {
        depth += 1;
      } else if (code[index] === '(') {
        if (depth === 0) {
          return line.from + index;
        }
        depth -= 1;
      }
    }
  }
  return null;
}

/** The parenthesis that closes the one at `open`, if it is closed at all. */
function closingBracket(doc: Text, open: number): number | null {
  const first = doc.lineAt(open);
  let depth = 0;
  for (let number = first.number; number <= doc.lines; number += 1) {
    const line = doc.line(number);
    const code = codeBefore(line.text, line.text.length);
    const from = number === first.number ? open - line.from : 0;
    for (let index = from; index < code.length; index += 1) {
      if (code[index] === '(') {
        depth += 1;
      } else if (code[index] === ')') {
        depth -= 1;
        if (depth === 0) {
          return line.from + index;
        }
      }
    }
  }
  return null;
}

/**
 * Whether a break in a line indents the line it opens.
 *
 * It does when there is a term to continue: when what is left after the break
 * comes down with it, which is a line being split, or when the code before the
 * break is still waiting for one. A break that comes at the end of a term, and
 * one that moves nothing but a comment, open a line at the same indentation.
 *
 * The comment is taken off the whole line first, so that a caret inside one
 * has nothing after it, and the rest is split at the column the break is at.
 */
/**
 * The indentation a line opened under `line` at `column` should have.
 *
 * A line at the margin moves in one level, so that what continues the term is
 * read as a continuation rather than as the next definition. A line already
 * inside a term keeps the level it was at, moved by the brackets the line
 * itself opens before the column: one still open takes the new line a level
 * deeper, and one closed takes it a level back. It never comes back to the
 * margin, where the parser would read a new definition.
 */
export function indentationFor(line: string, column: number, unit: number): number {
  const level = Math.floor((/^ */.exec(line)?.[0].length ?? 0) / unit);
  const moved = level + nestingAt(line, column);
  const wanted = level === 0 && moved <= level ? 1 : Math.max(moved, 1);
  return wanted * unit;
}

/**
 * The brackets left open at `column`, with any comment taken off.
 *
 * Only this line is counted, so a line continuing inside a bracket that was
 * opened above it has opened nothing of its own and keeps its level.
 */
function nestingAt(text: string, column: number): number {
  const code = text.replace(/--.*$/, '');
  let depth = 0;
  for (const character of code.slice(0, Math.max(0, Math.min(column, code.length)))) {
    if (character === '(') {
      depth += 1;
    } else if (character === ')') {
      depth -= 1;
    }
  }
  return depth;
}

/**
 * Whether the break ends the whole definition the caret is in.
 *
 * A definition is written over one line or several, and the lines after the
 * first are indented under the name that opened it. When the term is whole -
 * every bracket closed, and nothing left waiting for what comes next - the
 * definition is finished, wherever in the file its last line happens to sit.
 */
export function endsDefinition(doc: Text, at: number): boolean {
  const caret = doc.lineAt(at);
  // The definition starts at the nearest line above that is not indented: a
  // continuation is, and that is exactly how the parser tells them apart.
  let first = caret;
  while (first.number > 1 && /^[ \t]/.test(first.text)) {
    first = doc.line(first.number - 1);
  }
  // The lines are read as one term, so a bracket opened on one of them and
  // closed on another balances, and a comment is taken off each of them.
  let code = '';
  for (let number = first.number; number <= caret.number; number += 1) {
    const line = doc.line(number);
    const upTo = number === caret.number ? at - line.from : line.text.length;
    code += `${codeBefore(line.text, upTo)} `;
  }
  return !unfinished(code);
}

export function indentsAfter(line: string, column: number): boolean {
  const code = line.replace(/--.*$/, '');
  return code.slice(column).trim() !== '' || unfinished(code.slice(0, column));
}

/**
 * Whether the code up to a line break is still waiting for a term.
 *
 * Three things wait: an `=` with nothing after it, a lambda whose body has not
 * started, and an unclosed parenthesis, counted below. Anything else is a term
 * that could end there, and the line after it is the player's to indent.
 */
function unfinished(code: string): boolean {
  if (/(?:=|\.|\\)[ \t]*$/.test(code)) {
    return true;
  }
  let depth = 0;
  for (const character of code) {
    if (character === '(') {
      depth += 1;
    } else if (character === ')') {
      depth -= 1;
    }
  }
  return depth > 0;
}
